import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { 
  Navigation, Route, Plus, ChevronLeft, ChevronRight,
  MapPin, Clock, Euro, Loader2, Search, Filter,
  Calendar, Car, ExternalLink, Trash2, Edit, MoreVertical
} from 'lucide-react';

const OptiTrajetTrips = () => {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [mounted, setMounted] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchTrips = useCallback(async () => {
    const token = localStorage.getItem('optitrajet_token');
    if (!token) {
      navigate('/optitrajet/login');
      return;
    }

    try {
      const [tripsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/optitrajet/trips?limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/optitrajet/trips/stats?period=month`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (tripsRes.ok) {
        const tripsData = await tripsRes.json();
        setTrips(tripsData.trips || []);
      }
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (err) {
      console.error('Fetch trips error:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, navigate]);

  useEffect(() => {
    setMounted(true);
    fetchTrips();
  }, [fetchTrips]);

  const deleteTrip = async (tripId) => {
    if (!window.confirm('Supprimer ce trajet ?')) return;
    
    const token = localStorage.getItem('optitrajet_token');
    try {
      await fetch(`${API_URL}/api/optitrajet/trips/${tripId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTrips(trips.filter(t => t.id !== tripId));
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const filteredTrips = trips.filter(trip => 
    trip.start_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    trip.end_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    trip.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return "Hier";
    if (days < 7) return `Il y a ${days} jours`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8FAFC' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 lg:pb-8" style={{ background: '#F8FAFC', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/optitrajet/dashboard')}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-slate-600" />
            </button>
            <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800">
              Mes trajets
            </h1>
          </div>
          <Link to="/optitrajet/trips/new">
            <Button className="bg-blue-600 hover:bg-blue-700 rounded-xl h-10">
              <Plus className="h-4 w-4 mr-1" />
              Nouveau
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Stats Summary */}
        {stats && (
          <div className={`grid grid-cols-3 gap-3 mb-6 transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {stats.total_trips}
                </p>
                <p className="text-xs text-blue-600">Trajets</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-green-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {stats.total_distance_km}
                </p>
                <p className="text-xs text-emerald-600">km parcourus</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm bg-gradient-to-br from-violet-50 to-purple-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-violet-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {stats.total_revenue}€
                </p>
                <p className="text-xs text-violet-600">Revenus</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search */}
        <div className={`mb-6 transition-all duration-500 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Rechercher un trajet..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-xl border-slate-200"
            />
          </div>
        </div>

        {/* Trips List */}
        <div className={`space-y-3 transition-all duration-500 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {filteredTrips.length > 0 ? (
            filteredTrips.map((trip, idx) => (
              <Card 
                key={trip.id}
                className="border border-slate-100 shadow-sm hover:shadow-md transition-all overflow-hidden"
                style={{ animationDelay: `${idx * 30}ms` }}
              >
                <CardContent className="p-0">
                  <div className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Route className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-slate-400">{formatDate(trip.created_at)}</span>
                          {trip.client_name && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                              {trip.client_name}
                            </span>
                          )}
                        </div>
                        <p className="font-medium text-slate-800 mb-1">
                          {trip.start_address}
                        </p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <ChevronRight className="h-4 w-4" />
                          {trip.end_address || 'Destination non définie'}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p style={{ fontFamily: "'Outfit', sans-serif" }} className="font-bold text-lg text-emerald-600">
                          {trip.amount} €
                        </p>
                      </div>
                    </div>
                    
                    {/* Trip details */}
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                      <span className="flex items-center gap-1 text-sm text-slate-500">
                        <Navigation className="h-4 w-4" />
                        {trip.distance_km} km
                      </span>
                      <span className="flex items-center gap-1 text-sm text-slate-500">
                        <Clock className="h-4 w-4" />
                        {trip.duration_minutes} min
                      </span>
                      {trip.vehicle_id && (
                        <span className="flex items-center gap-1 text-sm text-slate-500">
                          <Car className="h-4 w-4" />
                          Véhicule
                        </span>
                      )}
                      <div className="flex-1" />
                      <button
                        onClick={() => deleteTrip(trip.id)}
                        className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border border-dashed border-slate-200 bg-slate-50/50">
              <CardContent className="p-8 text-center">
                <Route className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">
                  {searchQuery ? 'Aucun trajet trouvé' : 'Aucun trajet enregistré'}
                </p>
                <Link to="/optitrajet/trips/new">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Créer un trajet
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Floating Action Button - Mobile */}
      <Link to="/optitrajet/trips/new" className="lg:hidden">
        <button className="fixed bottom-20 right-4 w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-300 text-white z-50 transition-transform hover:scale-105 active:scale-95">
          <Plus className="h-7 w-7" />
        </button>
      </Link>
    </div>
  );
};

export default OptiTrajetTrips;
